import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Activity, Timer, Users, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Iter187 — Post-event analytics for a festival. Live-refreshable so
// coaches can watch metrics tick up while games run.
function fmtSec(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}m ${sec.toString().padStart(2, "0")}s`;
}

const BAND_INK = {
  green: "text-[#CCFF00]",
  blue: "text-[#66C2FF]",
  red: "text-[#FF4500]",
  unrated: "text-[#525252]",
  spare: "text-[#FFB020]",
};

function Stat({ label, value, icon, testid }) {
  return (
    <div className="card-surface p-4 flex-1 min-w-[140px]" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3]">
        {icon} {label}
      </div>
      <div className="h-display text-3xl mt-2">{value}</div>
    </div>
  );
}

export default function FestivalAnalytics() {
  const { id } = useParams();
  const [festival, setFestival] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const [f, a] = await Promise.all([
        api.get(`/festival/${id}`),
        api.get(`/festival/${id}/analytics`),
      ]);
      setFestival(f.data);
      setData(a.data);
    } catch {
      toast.error("Failed to load analytics");
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [id]);

  if (!festival || !data) return <div className="p-8 text-sm text-[#A3A3A3]">Loading analytics…</div>;

  const noShowPct = Math.round((data.no_show_rate || 0) * 100);
  const rosterUsed = data.roster_total - data.no_show_count;

  return (
    <div className="max-w-[1200px] mx-auto px-4 py-6" data-testid="festival-analytics">
      <Link to={`/festival/${id}`} className="text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> {festival.name}
      </Link>
      <div className="mb-6">
        <div className="label-mono text-[#CCFF00] mb-1">FESTIVAL · ANALYTICS</div>
        <h1 className="h-display text-3xl">Post-event snapshot</h1>
        <p className="text-sm text-[#A3A3A3] mt-1">Refreshes every 15 s · updated {new Date(data.generated_at).toLocaleTimeString()}</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Stat testid="stat-games" label="Games played" value={data.games_played} icon={<Activity className="w-3 h-3" />} />
        <Stat testid="stat-avg" label="Avg game" value={fmtSec(data.avg_game_length_s)} icon={<Timer className="w-3 h-3" />} />
        <Stat testid="stat-roster" label="Roster" value={data.roster_total} icon={<Users className="w-3 h-3" />} />
        <Stat testid="stat-noshow" label="No-shows" value={`${data.no_show_count} (${noShowPct}%)`} icon={<TrendingUp className="w-3 h-3" />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="card-surface p-4" data-testid="analytics-band-mix">
          <div className="label-mono text-[#CCFF00] mb-3">BAND MIX (games)</div>
          {data.band_mix.length === 0 ? (
            <div className="text-xs text-[#525252] italic">No games logged yet.</div>
          ) : (
            <ul className="space-y-1">
              {data.band_mix.map((row) => {
                const bands = (row.bands || "").split("+");
                return (
                  <li key={row.bands} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1">
                      {bands.map((b, i) => (
                        <span key={i} className={`text-[10px] uppercase tracking-wider ${BAND_INK[b] || BAND_INK.unrated}`}>{b}</span>
                      ))}
                    </span>
                    <span className="text-[#A3A3A3]">{row.games}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="card-surface p-4" data-testid="analytics-court-load">
          <div className="label-mono text-[#CCFF00] mb-3">COURT LOAD</div>
          {data.courts.length === 0 ? (
            <div className="text-xs text-[#525252] italic">No games logged yet.</div>
          ) : (
            <ul className="space-y-1">
              {data.courts.map((row) => (
                <li key={row.court} className="flex items-center justify-between text-sm">
                  <span>{row.court}</span>
                  <span className="text-[#A3A3A3]">{row.games}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card-surface p-4" data-testid="analytics-top-players">
          <div className="label-mono text-[#CCFF00] mb-3">MOST GAMES</div>
          {data.top_players.length === 0 ? (
            <div className="text-xs text-[#525252] italic">No games logged yet.</div>
          ) : (
            <ol className="space-y-1 text-sm">
              {data.top_players.map((p, i) => (
                <li key={p.id} className="flex items-center justify-between">
                  <span><span className="text-[#525252] mr-2">{i + 1}.</span>{p.name}</span>
                  <span className="text-[#A3A3A3]">{p.games}</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="card-surface p-4" data-testid="analytics-status-mix">
          <div className="label-mono text-[#CCFF00] mb-3">ROSTER STATUS</div>
          <ul className="space-y-1 text-sm">
            {Object.entries(data.status_counts).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between">
                <span className="uppercase text-[10px] tracking-wider text-[#A3A3A3]">{k}</span>
                <span>{v}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t border-[#262626] text-[11px] text-[#A3A3A3]">
            {rosterUsed} of {data.roster_total} players got court time.
          </div>
        </section>
      </div>
    </div>
  );
}
